import type { CSSProperties } from "react";
import { Link } from "wouter";
import { ArrowRight, Heart, Store, Radio, CalendarCheck, Star, Award, Phone } from "lucide-react";
import laMirageLogo from "@assets/download.png";

/**
 * lamirage.co logged-out landing — "The Gateway" (v2, 2026-07-13).
 *
 * NOT a marketing scroll (that's la-mirage.in's job) and deliberately NOT the
 * screenshot-marquee look of la-mirage.in/platform. This is an app launcher:
 * lamirage.co serves four different visitors — couples (login), vendors
 * (signup/login), invited guests (live streams), and people still choosing a
 * venue — so the page is four doors, each going to the right place.
 * Login flow untouched: Login links to /login exactly as before.
 * Static page — no queries, no auth logic.
 */

const serif: CSSProperties = { fontFamily: '"Playfair Display", Georgia, serif' };

const goldGrad: CSSProperties = {
  background: "linear-gradient(120deg,#f6dc9a,#efb700 55%,#c98f00)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

const goldFill: CSSProperties = {
  background: "linear-gradient(120deg,#f6dc9a,#efb700 55%,#c98f00)",
};

export default function Home() {
  return (
    <div className="min-h-dvh bg-[#07070b] text-[#e8e4da] antialiased overflow-x-clip">
      <style>{`
        @keyframes lmFloat{0%,100%{transform:translateY(0) rotate(-1.2deg)}50%{transform:translateY(-14px) rotate(-1.2deg)}}
        @keyframes lmIn{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:none}}
        .lm-in{animation:lmIn .7s ease both}
        .lm-frame{animation:lmFloat 7s ease-in-out infinite}
        @media (prefers-reduced-motion:reduce){.lm-in,.lm-frame{animation:none}}
      `}</style>

      {/* aurora */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden>
        <div className="absolute -left-[14vw] -top-[18vw] h-[52vw] w-[52vw] rounded-full opacity-50 blur-[90px]" style={{ background: "radial-gradient(circle,rgba(239,183,0,.15),transparent 65%)" }} />
        <div className="absolute -right-[12vw] top-[30vh] h-[44vw] w-[44vw] rounded-full opacity-50 blur-[90px]" style={{ background: "radial-gradient(circle,rgba(120,80,220,.09),transparent 65%)" }} />
      </div>

      {/* nav */}
      <nav className="sticky top-0 z-50 flex items-center justify-between border-b border-white/[0.06] bg-[#07070b]/70 px-5 py-3 backdrop-blur-xl md:px-8">
        <div className="flex items-center gap-3">
          <img src={laMirageLogo} alt="LA Mirage" className="h-10 w-10 rounded-lg object-contain" draggable={false} />
          <div className="leading-tight">
            <div className="text-[15px] font-bold tracking-[0.18em] text-white">LA MIRAGE</div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#8b867c]">lamirage.co</div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link href="/login">
            <a className="rounded-full px-6 py-2.5 text-sm font-bold text-[#100c02] shadow-[0_8px_30px_rgba(239,183,0,0.25)] transition-transform hover:-translate-y-0.5" style={goldFill}>
              Login
            </a>
          </Link>
        </div>
      </nav>

      {/* gateway hero: copy left, floating app frame right */}
      <header className="mx-auto grid max-w-6xl items-center gap-10 px-6 pb-6 pt-14 md:grid-cols-[1.1fr_1fr] md:pt-20">
        <div className="lm-in text-center md:text-left">
          <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#efb700]">
            The LA Mirage Platform
          </span>
          <h1 className="mt-4 text-4xl font-extrabold leading-[1.08] tracking-tight text-white md:text-[3.4rem]">
            One venue.{" "}
            <span style={{ ...serif, ...goldGrad }} className="italic">
              Everyone&apos;s door.
            </span>
          </h1>
          <p className="mt-5 max-w-md text-[#cfc9bd] md:text-lg mx-auto md:mx-0">
            Couples, vendors, guests, families — everything at LA Mirage runs through here. Pick
            your door below, or log straight in.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3 md:justify-start">
            <Link href="/login">
              <a className="rounded-full px-9 py-3.5 text-base font-bold text-[#100c02] shadow-[0_8px_30px_rgba(239,183,0,0.3)] transition-transform hover:-translate-y-0.5" style={goldFill}>
                Login
              </a>
            </Link>
            <span className="text-xs uppercase tracking-[0.18em] text-[#8b867c]">
              1000+ events · since 2022
            </span>
          </div>
        </div>
        <div className="lm-frame relative hidden md:block" aria-hidden>
          <div className="absolute inset-0 -z-10 translate-y-6 scale-95 rounded-3xl bg-[#efb700]/10 blur-3xl" />
          <div className="overflow-hidden rounded-2xl border border-[#efb700]/25 shadow-[0_40px_90px_rgba(0,0,0,0.7)]">
            <div className="flex items-center gap-1.5 border-b border-white/10 bg-[#101016] px-4 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ef4444]/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#f59e0b]/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#10b981]/70" />
              <span className="ml-3 rounded-md bg-white/5 px-3 py-0.5 text-[10px] tracking-wide text-[#8b867c]">
                lamirage.co — your wedding dashboard
              </span>
            </div>
            <img
              src="/platform/shot-dashboard-w.webp"
              alt=""
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              className="block w-full select-none"
            />
          </div>
        </div>
      </header>

      {/* the four doors */}
      <main className="mx-auto max-w-6xl px-6 pb-8 pt-8 md:pt-12">
        <div className="grid gap-5 md:grid-cols-2">
          {/* couples — primary */}
          <a href="/login" className="lm-in group relative block overflow-hidden rounded-3xl border border-[#efb700]/40 bg-gradient-to-br from-[#efb700]/[0.14] to-white/[0.02] p-7 transition-all hover:-translate-y-1 hover:border-[#efb700]/70 hover:shadow-[0_20px_60px_rgba(239,183,0,0.15)] md:p-9" style={{ animationDelay: ".05s" }}>
              <Heart className="h-8 w-8 text-[#efb700]" />
              <h2 className="mt-4 text-2xl font-extrabold text-white">
                I&apos;m getting married <span style={{ ...serif, ...goldGrad }} className="italic">here.</span>
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-[#cfc9bd]">
                Your wedding dashboard — live stream, AI photos, website, invitations, guests,
                budget and 22+ tools. Everything about your day, one login.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold text-[#100c02]" style={goldFill}>
                Login to your wedding <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
          </a>

          {/* vendors — restored, full pitch */}
          <a href="/vendor-signup" className="lm-in group relative block overflow-hidden rounded-3xl border border-white/[0.1] bg-white/[0.028] p-7 transition-all hover:-translate-y-1 hover:border-[#efb700]/50 md:p-9" style={{ animationDelay: ".12s" }}>
              <Store className="h-8 w-8 text-[#efb700]" />
              <h2 className="mt-4 text-2xl font-extrabold text-white">
                I&apos;m a wedding <span style={{ ...serif, ...goldGrad }} className="italic">vendor.</span>
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-[#cfc9bd]">
                Partner with LA Mirage — premium exposure to couples planning luxury weddings, and
                verified status in our approved directory.
              </p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#8b867c]">
                <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 text-[#efb700]" /> Premium exposure</span>
                <span className="inline-flex items-center gap-1"><Award className="h-3 w-3 text-[#efb700]" /> Verified status</span>
                <span>Photography · Catering · Décor · Music</span>
              </div>
              <span className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#efb700]/50 px-6 py-2.5 text-sm font-bold text-[#efb700] transition-colors group-hover:bg-[#efb700]/10">
                Join our vendor network <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
          </a>

          {/* guests */}
          <a
            href="https://lamirage.live/"
            target="_blank"
            rel="noopener noreferrer"
            className="lm-in group relative block overflow-hidden rounded-3xl border border-white/[0.1] bg-white/[0.028] p-7 transition-all hover:-translate-y-1 hover:border-[#efb700]/50 md:p-9"
            style={{ animationDelay: ".19s" }}
          >
            <Radio className="h-8 w-8 text-[#efb700]" />
            <h2 className="mt-4 text-2xl font-extrabold text-white">
              I&apos;m invited to a <span style={{ ...serif, ...goldGrad }} className="italic">wedding.</span>
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[#cfc9bd]">
              Watch weddings live in 4K on LA Mirage Live, send your wishes, and find your photos.
              RSVP and galleries open from the link the couple sent you.
            </p>
            <span className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-2.5 text-sm font-bold text-[#e8e4da] transition-colors group-hover:border-[#efb700]/50 group-hover:text-[#efb700]">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" /></span>
              Watch live — lamirage.live <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </a>

          {/* planning */}
          <a
            href="https://la-mirage.in/"
            target="_blank"
            rel="noopener noreferrer"
            className="lm-in group relative block overflow-hidden rounded-3xl border border-white/[0.1] bg-white/[0.028] p-7 transition-all hover:-translate-y-1 hover:border-[#efb700]/50 md:p-9"
            style={{ animationDelay: ".26s" }}
          >
            <CalendarCheck className="h-8 w-8 text-[#efb700]" />
            <h2 className="mt-4 text-2xl font-extrabold text-white">
              I&apos;m still choosing a <span style={{ ...serif, ...goldGrad }} className="italic">venue.</span>
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[#cfc9bd]">
              See LA Mirage Koratty — the venue, real weddings, transparent pricing, and live date
              availability. Book it, and this whole platform comes with it.
            </p>
            <span className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-2.5 text-sm font-bold text-[#e8e4da] transition-colors group-hover:border-[#efb700]/50 group-hover:text-[#efb700]">
              Visit la-mirage.in <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </a>
        </div>

        {/* slim utility strip */}
        <div className="lm-in mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 rounded-2xl border border-white/[0.07] bg-white/[0.02] px-6 py-4 text-sm text-[#8b867c]" style={{ animationDelay: ".33s" }}>
          <a href="tel:+919446776666" className="inline-flex items-center gap-2 font-semibold text-[#cfc9bd] hover:text-[#efb700]">
            <Phone className="h-4 w-4 text-[#efb700]" /> +91 94467 76666
          </a>
        </div>
      </main>

      {/* footer */}
      <footer className="mt-6 border-t border-white/[0.07] px-6 py-8 text-center text-xs text-[#8b867c]">
        {/* Company + policy pages are server-rendered (server/publicPages) — plain <a>, full
            page load, so gateway/bank reviewers and crawlers see them without JavaScript. */}
        <p>
          <a href="/about" className="text-[#efb700] hover:underline">About</a>
          {" · "}
          <a href="/contact" className="text-[#efb700] hover:underline">Contact</a>
          {" · "}
          <a href="/privacy-policy" className="text-[#efb700] hover:underline">Privacy Policy</a>
          {" · "}
          <a href="/terms-and-conditions" className="text-[#efb700] hover:underline">Terms &amp; Conditions</a>
          {" · "}
          <a href="/refund-policy" className="text-[#efb700] hover:underline">Refund &amp; Cancellation Policy</a>
          {" · "}
          <a href="https://la-mirage.in/" target="_blank" rel="noopener noreferrer" className="text-[#efb700] hover:underline">Venue website</a>
        </p>
        <p className="mt-3 text-[#8b867c]">
          LA Mirage Convention Center · Manjalaykettu, Old NH, Koratty, Kerala 680308, India · GSTIN 32AAIFL4120G1ZT
        </p>
        <p className="mt-1 text-[#8b867c]">
          <a href="tel:+919446776666" className="hover:underline">+91 9446 77 6666</a>
          {" · "}
          <a href="mailto:sales@lamirage.co" className="hover:underline">sales@lamirage.co</a>
        </p>
        <p className="mt-2 text-[#6d685f]">© 2022–2026 LA Mirage Koratty. The platform, its software and design are proprietary.</p>
      </footer>
    </div>
  );
}
